/**
 * A delivery profile is a collection of delivery regions that define shipping options for products.
 * Each profile contains delivery regions, which specify where products can be shipped and which carriers can deliver to those destinations.
 */
export interface DeliveryProfile {
  /**
   * Delivery profile ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Delivery profile name.
   * @minLength 1
   * @maxLength 256
   */
  name?: string | null;
  /**
   * Whether this is the default delivery profile.
   *
   * > **Notes:**
   * >
   * > - The first delivery profile is automatically created and marked as default when the Wix Stores, Wix Bookings, Wix Events, or Wix Restaurants business solutions are installed on a site.
   * > - Default status can't be changed or transferred to another profile.
   * > - The default profile can't be deleted because every site requires one for core delivery functionality.
   * @readonly
   */
  default?: boolean | null;
  /**
   * Delivery regions in this profile.
   * @maxSize 100
   */
  deliveryRegions?: DeliveryRegion[];
  /**
   * Information about who created the delivery profile.
   *
   * Currently only for use with Wix Restaurants.
   */
  createdBy?: CreatedBy;
  /**
   * Revision number, which increments by 1 each time the delivery profile is updated. To prevent conflicting changes, the current revision must be passed when updating the delivery profile. Ignored when creating a delivery profile.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the delivery profile was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the delivery profile was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /** Custom field data for the delivery profile object. [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls. */
  extendedFields?: ExtendedFields;
}

/**
 * A delivery region defines a geographic area and its associated delivery carriers.
 * Each region specifies destinations (countries and subdivisions) where products can be shipped and the carriers available for those deliveries.
 *
 * During the eCommerce onboarding flow, an "International" region with empty destinations is automatically created. This serves as a "Rest of World" configuration that applies to destinations not explicitly defined in other regions.
 */
export interface DeliveryRegion {
  /**
   * Delivery region ID.
   * @format GUID
   * @readonly
   */
  deliveryRegionId?: string | null;
  /**
   * Delivery region name. For example, `"Domestic"` or `"International"`.
   * @minLength 1
   * @maxLength 256
   */
  name?: string | null;
  /**
   * Whether this delivery region is active and available during checkout.
   *
   * Default: `true`
   */
  active?: boolean | null;
  /**
   * Delivery carriers assigned to this region. Carriers are managed using the [Add Delivery Carrier](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/add-delivery-carrier), [Update Delivery Carrier](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/update-delivery-carrier), and [Remove Delivery Carrier](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/remove-delivery-carrier) methods.
   * @maxSize 25
   * @readonly
   */
  deliveryCarriers?: DeliveryCarrier[];
  /**
   * Geographic destinations covered by this region. If empty, the region applies globally (Rest of World). A site can have up to 250 destinations across all delivery profiles.
   * @maxSize 250
   */
  destinations?: Destination[];
  /**
   * Date and time the delivery region was created.
   * @readonly
   */
  createdDate?: Date | null;
}

export interface DeliveryCarrier {
  /**
   * Carrier app ID.
   *
   * Get app IDs from the [app dashboard](https://dev.wix.com/dc3/my-apps/), or by calling [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).
   * @format GUID
   */
  appId?: string;
  /**
   * Backup rate to use when the carrier doesn't return a rate for a specific shipping option.
   *
   * When `active` is `false` and the carrier doesn't return a rate, the carrier's shipping options aren't shown to customers.
   */
  backupRate?: BackupRate;
  /**
   * Additional charges to add to the delivery rate. These charges are combined with the carrier's rate into a single shipping cost and itemized in order details.
   * @maxSize 10
   */
  additionalCharges?: AdditionalCharge[];
}

export interface BackupRate {
  /**
   * Title displayed for the backup rate. For example, `"Standard Shipping"`.
   * @minLength 1
   * @maxLength 256
   */
  title?: string | null;
  /**
   * Backup rate amount.
   * @decimalValue options { gte:0, maxScale:6 }
   */
  amount?: string | null;
  /**
   * Whether the backup rate is active.
   *
   * The backup rate is used in the following situations:
   * + When `active` is `true`.
   * + When the carrier does not return a cost for any reason.
   *
   * If active, and no cost is returned by the carrier, the backup rate will be displayed to customers as the shipping option.
   *
   * Default: `false`
   */
  active?: boolean;
}

export interface AdditionalCharge {
  /**
   * Description of the additional charge. For example, `"Handling fee of $5 applied for gift wrapping"`.
   * @maxLength 250
   */
  description?: string | null;
  /**
   * Additional fixed charge amount to add to the delivery rate. For example, `"12.50"`.
   * @maxLength 16
   */
  amount?: string;
}

/** Geographic destination for a delivery region. */
export interface Destination {
  /**
   * 2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.
   * @format COUNTRY
   */
  countryCode?: string;
  /**
   * Subdivision codes in [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format. For example, `"US-CA"` for California. If empty, the delivery region applies to the entire country.
   *
   * The following countries don't support subdivision-level delivery and require an empty `subdivisions` list. Sending subdivisions for these countries fails with `SUBDIVISIONS_UNSUPPORTED_FOR_COUNTRY`:
   * `AT`, `BE`, `BS`, `CH`, `CI`, `CZ`, `DO`, `GR`, `NO`, `PL`, `RS`, `SD`, `SI`, `SK`, `SN`.
   * @maxSize 100
   * @minLength 1
   * @maxLength 256
   */
  subdivisions?: string[];
}

export interface CreatedBy extends CreatedByIdOneOf {
  /**
   * App ID, when the delivery profile was created by an external application or Wix service.
   * @format GUID
   */
  appId?: string | null;
  /**
   * Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.
   * @format GUID
   */
  userId?: string | null;
}

/** @oneof */
export interface CreatedByIdOneOf {
  /**
   * App ID, when the delivery profile was created by an external application or Wix service.
   * @format GUID
   */
  appId?: string | null;
  /**
   * Wix user ID, when the delivery profile was created by a Wix user using the dashboard or an API call.
   * @format GUID
   */
  userId?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/** Event payload for when a delivery region is added to a delivery profile. */
export interface DeliveryRegionAdded {
  /**
   * ID of the delivery profile containing the region.
   * @format GUID
   */
  deliveryProfileId?: string;
  /**
   * ID of the added delivery region.
   * @format GUID
   * @readonly
   */
  deliveryRegionId?: string | null;
  /**
   * Delivery region name. For example, `"Domestic"`.
   * @minLength 1
   * @maxLength 256
   */
  name?: string | null;
  /** Whether the delivery region is active. */
  active?: boolean | null;
  /**
   * Delivery carriers assigned to this region.
   * @maxSize 25
   * @readonly
   */
  deliveryCarriers?: DeliveryCarrier[];
  /**
   * Geographic destinations covered by this region.
   * @maxSize 200
   */
  destinations?: Destination[];
  /**
   * Date and time the delivery region was created.
   * @readonly
   */
  createdDate?: Date | null;
}

/** Event payload for when a delivery region is removed from a delivery profile. */
export interface DeliveryRegionRemoved {
  /**
   * ID of the delivery profile that contained the region.
   * @format GUID
   */
  deliveryProfileId?: string;
  /**
   * ID of the removed delivery region.
   * @format GUID
   */
  deliveryRegionId?: string;
}

/** Event payload for when a delivery region is updated. */
export interface DeliveryRegionUpdated {
  /**
   * ID of the delivery profile containing the region.
   * @format GUID
   */
  deliveryProfileId?: string;
  /**
   * ID of the updated delivery region.
   * @format GUID
   * @readonly
   */
  deliveryRegionId?: string | null;
  /**
   * Delivery region name. For example, `"Domestic"`.
   * @minLength 1
   * @maxLength 256
   */
  name?: string | null;
  /** Whether the delivery region is active. */
  active?: boolean | null;
  /**
   * Delivery carriers assigned to this region.
   * @maxSize 25
   * @readonly
   */
  deliveryCarriers?: DeliveryCarrier[];
  /**
   * Geographic destinations covered by this region. If empty, the region applies globally.
   * @maxSize 200
   */
  destinations?: Destination[];
  /**
   * Date and time the delivery region was created.
   * @readonly
   */
  createdDate?: Date | null;
}

export interface CreateDeliveryProfileRequest {
  /** Delivery profile to create. */
  deliveryProfile: DeliveryProfile;
}

export interface CreateDeliveryProfileResponse {
  /** Created delivery profile. */
  deliveryProfile?: DeliveryProfile;
}

export interface GetDeliveryProfileRequest {
  /**
   * Delivery profile ID.
   * @format GUID
   */
  deliveryProfileId: string;
}

export interface GetDeliveryProfileResponse {
  /** Retrieved delivery profile. */
  deliveryProfile?: DeliveryProfile;
}

export interface UpdateDeliveryProfileRequest {
  /** Delivery profile to update. */
  deliveryProfile: DeliveryProfile;
}

export interface UpdateDeliveryProfileResponse {
  /** Updated delivery profile. */
  deliveryProfile?: DeliveryProfile;
}

export interface DeleteDeliveryProfileRequest {
  /**
   * Delivery profile ID.
   * @format GUID
   */
  deliveryProfileId: string;
}

export interface DeleteDeliveryProfileResponse {}

export interface DeleteDefaultDeliveryProfileRequest {
  /**
   * Delivery profile ID.
   * @format GUID
   */
  deliveryProfileId?: string;
}

export interface DeleteDefaultDeliveryProfileResponse {}

export interface QueryDeliveryProfilesRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryDeliveryProfilesResponse {
  /**
   * List of delivery profiles.
   * @maxSize 100
   */
  deliveryProfiles?: DeliveryProfile[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface AddDeliveryRegionRequest {
  /**
   * Delivery profile ID.
   * @format GUID
   */
  deliveryProfileId: string;
  /** Delivery region to add. */
  deliveryRegion: DeliveryRegion;
  /**
   * Delivery profile revision.
   * @readonly
   */
  revision?: string | null;
}

export interface AddDeliveryRegionResponse {
  /** Updated delivery profile with the new delivery region. */
  deliveryProfile?: DeliveryProfile;
}

export interface UpdateDeliveryRegionRequest {
  /**
   * Delivery profile ID.
   * @format GUID
   */
  deliveryProfileId: string;
  /** Delivery region to update. */
  deliveryRegion: DeliveryRegion;
  /**
   * Delivery profile revision.
   * @readonly
   */
  revision?: string | null;
}

export interface UpdateDeliveryRegionResponse {
  /** Updated delivery profile with the updated delivery region. */
  deliveryProfile?: DeliveryProfile;
}

export interface GetDeliveryProfileByDeliveryRegionIdRequest {
  /**
   * Delivery region ID.
   * @format GUID
   */
  deliveryRegionId: string;
}

export interface GetDeliveryProfileByDeliveryRegionIdResponse {
  /** Delivery profile containing the requested region. */
  deliveryProfile?: DeliveryProfile;
}

export interface RemoveDeliveryRegionRequest {
  /**
   * Delivery profile ID.
   * @format GUID
   */
  deliveryProfileId: string;
  /**
   * Delivery region ID.
   * @format GUID
   */
  deliveryRegionId: string;
  /**
   * Delivery profile revision.
   * @readonly
   */
  revision?: string | null;
}

export interface RemoveDeliveryRegionResponse {
  /** Updated delivery profile with the delivery region removed. */
  deliveryProfile?: DeliveryProfile;
}

export interface AddDeliveryCarrierRequest {
  /**
   * Delivery region ID.
   * @format GUID
   */
  deliveryRegionId: string;
  /** Delivery carrier to add. */
  deliveryCarrier: DeliveryCarrier;
}

export interface AddDeliveryCarrierResponse {
  /** Updated delivery profile. */
  deliveryProfile?: DeliveryProfile;
}

export interface RemoveDeliveryCarrierRequest {
  /**
   * Delivery region ID.
   * @format GUID
   */
  deliveryRegionId: string;
  /**
   * App ID of the delivery carrier to remove.
   * @format GUID
   */
  appId: string;
}

export interface RemoveDeliveryCarrierResponse {
  /** Updated delivery profile. */
  deliveryProfile?: DeliveryProfile;
}

export interface UpdateDeliveryCarrierRequest {
  /**
   * Delivery region ID.
   * @format GUID
   */
  deliveryRegionId: string;
  /** Delivery carrier to update. */
  deliveryCarrier?: DeliveryCarrier;
}

export interface UpdateDeliveryCarrierResponse {
  /** Updated delivery profile with the updated delivery carrier. */
  deliveryProfile?: DeliveryProfile;
}

export interface ListInstalledDeliveryCarriersRequest {}

export interface ListInstalledDeliveryCarriersResponse {
  /**
   * List of installed delivery carriers on the site.
   * @maxSize 100
   */
  installedDeliveryCarriers?: InstalledDeliveryCarrier[];
}

export interface InstalledDeliveryCarrier {
  /**
   * Carrier app ID.
   * @format GUID
   */
  id?: string;
  /**
   * Carrier display name.
   * @maxLength 256
   */
  displayName?: string;
  /**
   * Carrier description.
   * @maxLength 200
   */
  description?: string | null;
  /**
   * URL to learn more about the carrier.
   * @maxLength 200
   */
  learnMoreUrl?: string | null;
  /**
   * URL to the carrier's dashboard.
   * @minLength 1
   * @maxLength 2048
   * @format WEB_URL
   */
  dashboardUrl?: string | null;
  /** Whether a backup rate is required for this carrier. */
  fallbackDefinitionMandatory?: boolean | null;
  /**
   * URL for the carrier's thumbnail image.
   * @format WEB_URL
   */
  thumbnailUrl?: string | null;
  /** Whether the carrier supports retrieving carrier settings. */
  toggleGetCarrierSettingsEnabled?: boolean;
}

export interface ListDeliveryDestinationsRequest {}

export interface ListDeliveryDestinationsResponse {
  /** Scope of destinations. When `WORLDWIDE` is selected, the `destinations` list is empty. */
  destinationScope?: DestinationScopeWithLiterals;
  /**
   * List of configured destinations.
   * @maxSize 200
   */
  destinations?: Destination[];
}

export enum DestinationScope {
  /** Specific destinations are configured. */
  SPECIFIC_DESTINATIONS = 'SPECIFIC_DESTINATIONS',
  /** All destinations worldwide. */
  WORLDWIDE = 'WORLDWIDE',
}

/** @enumType */
export type DestinationScopeWithLiterals =
  | DestinationScope
  | 'SPECIFIC_DESTINATIONS'
  | 'WORLDWIDE';

export interface RequiredZipcodeRequest {}

export interface RequiredZipcodeResponse {
  /** Whether the site requires a postal code for delivery. */
  requiredZipcode?: boolean;
}

export interface GetDeliveryDestinationPropertiesRequest {}

export interface GetDeliveryDestinationPropertiesResponse {
  /** Delivery destination properties for Rest of World. */
  restOfWorld?: DeliveryDestinationProperties;
  /**
   * List of countries and their delivery destination properties.
   * @maxSize 195
   */
  countries?: CountryDeliveryProperties[];
}

export interface DeliveryDestinationProperties {
  /** Whether a postal code is required for delivery to this destination. */
  postalCodeRequired?: boolean;
  /** Whether only pickup is available at this destination. */
  pickupOnly?: boolean;
}

export interface CountryDeliveryProperties {
  /**
   * 2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.
   * @format COUNTRY
   */
  country?: string;
  /** Delivery destination properties for the country. */
  deliveryDestinationProperties?: DeliveryDestinationProperties;
  /**
   * List of subdivisions and their delivery destination properties.
   * @maxSize 100
   */
  subdivisions?: SubdivisionDeliveryProperties[];
}

export interface SubdivisionDeliveryProperties {
  /**
   * Code for a subdivision (such as state, prefecture, or province) in [ISO 3166-2](https://www.iso.org/standard/72483.html) format.
   * @minLength 1
   * @maxLength 5
   */
  subdivision?: string;
  /** Delivery destination properties for the subdivision. */
  deliveryDestinationProperties?: DeliveryDestinationProperties;
}

export interface ListDeliveryCarriersRequest {
  /**
   * Delivery profile ID.
   * @format GUID
   */
  deliveryProfileId: string;
  /**
   * App IDs of the delivery carriers to retrieve settings for.
   * @format GUID
   * @minSize 1
   * @maxSize 25
   */
  appIds?: string[];
}

export interface ListDeliveryCarriersResponse {
  /**
   * List of delivery carrier results.
   * @maxSize 100
   */
  results?: ListDeliveryCarriersResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ListDeliveryCarriersResult {
  /** Metadata for the result, including success or error information. */
  deliveryCarrierMetadata?: ItemMetadata;
  /** Delivery carrier details. */
  deliveryCarrierDetails?: DeliveryCarrierDetails;
  /**
   * Regional settings for the delivery carrier.
   * @minSize 1
   * @maxSize 5
   */
  deliveryCarrierRegionalSettings?: DeliveryCarrierRegionalSettings[];
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  id?: string | null;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface DeliveryCarrierDetails {
  /**
   * Carrier app ID.
   * @format GUID
   */
  id?: string;
  /**
   * Carrier display name.
   * @maxLength 256
   */
  displayName?: string;
  /**
   * Carrier description.
   * @maxLength 200
   */
  description?: string | null;
  /**
   * URL to learn more about the carrier.
   * @maxLength 200
   */
  learnMoreUrl?: string | null;
  /**
   * URL to the carrier's dashboard.
   * @minLength 1
   * @maxLength 2048
   * @format WEB_URL
   */
  dashboardUrl?: string | null;
  /** Whether a backup rate is required for this carrier. */
  fallbackDefinitionMandatory?: boolean | null;
  /**
   * URL for the carrier's thumbnail image.
   * @format WEB_URL
   */
  thumbnailUrl?: string | null;
  /** Whether the carrier supports retrieving carrier settings. */
  toggleGetCarrierSettingsEnabled?: boolean;
  /** Whether the carrier supports updating carrier active status. */
  toggleUpdateCarrierActiveStatusEnabled?: boolean;
}

export interface DeliveryCarrierRegionalSettings {
  /**
   * Delivery region ID.
   * @format GUID
   */
  deliveryRegionId?: string;
  /**
   * Dashboard tables with shipping configuration for this region. Multiple tables can exist if the carrier is an aggregator of multiple services.
   * @minSize 1
   * @maxSize 5
   */
  dashboardTables?: DashboardTable[];
}

export interface DashboardTable {
  /**
   * Table title.
   * @maxLength 256
   */
  title?: string;
  /**
   * Table columns.
   * @maxSize 200
   */
  columns?: Column[];
  /**
   * Table rows containing the configuration data.
   * @maxSize 25
   */
  rows?: Row[];
}

export interface Column {
  /**
   * Column key, used to retrieve data from rows.
   * @maxLength 256
   */
  key?: string;
  /**
   * Column display name.
   * @maxLength 256
   */
  name?: string;
}

export interface Row {
  /**
   * Unique row identifier, used to identify the row for editing.
   * @maxLength 256
   */
  key?: string;
  /** Row data as a JSON object. Each key corresponds to a column key. */
  data?: Record<string, any> | null;
  /** Whether this row's configuration is active. */
  active?: boolean;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface UpdateCarrierSettingsRequest {
  /**
   * Carrier app ID.
   * @format GUID
   */
  carrierId: string;
  /**
   * Row ID in the carrier's dashboard settings table that identifies the specific shipping configuration to update. Retrieved from [List Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-delivery-carriers).
   * @format GUID
   */
  rowId: string;
  /** New active status. */
  active: boolean | null;
}

export interface UpdateCarrierSettingsResponse {}

export interface SetDeliveryCarrierActiveStatusRequest {
  /**
   * Carrier app ID.
   * @format GUID
   */
  carrierAppId: string;
  /**
   * Row identifier in the carrier's dashboard settings table that identifies the specific shipping configuration to update.
   *
   * Pass the `row.key` field from the response of [List Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-delivery-carriers).
   * @format GUID
   */
  rowId: string;
  /** Whether the configuration defined in the row is active. */
  active: boolean | null;
}

export interface SetDeliveryCarrierActiveStatusResponse {}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Updated delivery profile. */
  deliveryProfile?: DeliveryProfile;
}

export interface MetaSiteSpecialEvent extends MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
  /**
   * A meta site id.
   * @format GUID
   */
  metaSiteId?: string;
  /** A meta site version. Monotonically increasing. */
  version?: string;
  /** A timestamp of the event. */
  timestamp?: string;
  /**
   * TODO(meta-site): Change validation once validations are disabled for consumers
   * More context: https://wix.slack.com/archives/C0UHEBPFT/p1720957844413149 and https://wix.slack.com/archives/CFWKX325T/p1728892152855659
   * @maxSize 4000
   */
  assets?: Asset[];
}

/** @oneof */
export interface MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
}

export interface Asset {
  /**
   * An application definition id (app_id in dev-center). For legacy reasons may be UUID or a string (from Java Enum).
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * An instance id. For legacy reasons may be UUID or a string.
   * @maxLength 200
   */
  instanceId?: string;
  /** An application state. */
  state?: StateWithLiterals;
}

export enum State {
  UNKNOWN = 'UNKNOWN',
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  PENDING = 'PENDING',
  DEMO = 'DEMO',
}

/** @enumType */
export type StateWithLiterals =
  | State
  | 'UNKNOWN'
  | 'ENABLED'
  | 'DISABLED'
  | 'PENDING'
  | 'DEMO';

export interface SiteCreated {
  /**
   * A template identifier (empty if not created from a template).
   * @maxLength 36
   */
  originTemplateId?: string;
  /**
   * An account id of the owner.
   * @format GUID
   */
  ownerId?: string;
  /** A context in which meta site was created. */
  context?: SiteCreatedContextWithLiterals;
  /**
   * A meta site id from which this site was created.
   *
   * In case of a creation from a template it's a template id.
   * In case of a site duplication ("Save As" in dashboard or duplicate in UM) it's an id of a source site.
   * @format GUID
   */
  originMetaSiteId?: string | null;
  /**
   * A meta site name (URL slug).
   * @maxLength 20
   */
  siteName?: string;
  /** A namespace. */
  namespace?: NamespaceWithLiterals;
}

export enum SiteCreatedContext {
  /** A valid option, we don't expose all reasons why site might be created. */
  OTHER = 'OTHER',
  /** A meta site was created from template. */
  FROM_TEMPLATE = 'FROM_TEMPLATE',
  /** A meta site was created by copying of the transfferred meta site. */
  DUPLICATE_BY_SITE_TRANSFER = 'DUPLICATE_BY_SITE_TRANSFER',
  /** A copy of existing meta site. */
  DUPLICATE = 'DUPLICATE',
  /** A meta site was created as a transfferred site (copy of the original), old flow, should die soon. */
  OLD_SITE_TRANSFER = 'OLD_SITE_TRANSFER',
  /** deprecated A meta site was created for Flash editor. */
  FLASH = 'FLASH',
}

/** @enumType */
export type SiteCreatedContextWithLiterals =
  | SiteCreatedContext
  | 'OTHER'
  | 'FROM_TEMPLATE'
  | 'DUPLICATE_BY_SITE_TRANSFER'
  | 'DUPLICATE'
  | 'OLD_SITE_TRANSFER'
  | 'FLASH';

export enum Namespace {
  UNKNOWN_NAMESPACE = 'UNKNOWN_NAMESPACE',
  /** Default namespace for UGC sites. MetaSites with this namespace will be shown in a user's site list by default. */
  WIX = 'WIX',
  /** ShoutOut stand alone product. These are siteless (no actual Wix site, no HtmlWeb). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  SHOUT_OUT = 'SHOUT_OUT',
  /** MetaSites created by the Albums product, they appear as part of the Albums app. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ALBUMS = 'ALBUMS',
  /** Part of the WixStores migration flow, a user tries to migrate and gets this site to view and if the user likes it then stores removes this namespace and deletes the old site with the old stores. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  WIX_STORES_TEST_DRIVE = 'WIX_STORES_TEST_DRIVE',
  /** Hotels standalone (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  HOTELS = 'HOTELS',
  /** Clubs siteless MetaSites, a club without a wix website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  CLUBS = 'CLUBS',
  /** A partially created ADI website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ONBOARDING_DRAFT = 'ONBOARDING_DRAFT',
  /** AppBuilder for AppStudio / shmite (c). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_SITE = 'DEV_SITE',
  /** LogoMaker websites offered to the user after logo purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  LOGOS = 'LOGOS',
  /** VideoMaker websites offered to the user after video purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  VIDEO_MAKER = 'VIDEO_MAKER',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  PARTNER_DASHBOARD = 'PARTNER_DASHBOARD',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_CENTER_COMPANY = 'DEV_CENTER_COMPANY',
  /**
   * A draft created by HTML editor on open. Upon "first save" it will be moved to be of WIX domain.
   *
   * Meta site with this namespace will *not* be shown in a user's site list by default.
   */
  HTML_DRAFT = 'HTML_DRAFT',
  /**
   * the user-journey for Fitness users who want to start from managing their business instead of designing their website.
   * Will be accessible from Site List and will not have a website app.
   * Once the user attaches a site, the site will become a regular wixsite.
   */
  SITELESS_BUSINESS = 'SITELESS_BUSINESS',
  /** Belongs to "strategic products" company. Supports new product in the creator's economy space. */
  CREATOR_ECONOMY = 'CREATOR_ECONOMY',
  /** It is to be used in the Business First efforts. */
  DASHBOARD_FIRST = 'DASHBOARD_FIRST',
  /** Bookings business flow with no site. */
  ANYWHERE = 'ANYWHERE',
  /** Namespace for Headless Backoffice with no editor */
  HEADLESS = 'HEADLESS',
  /**
   * Namespace for master site that will exist in parent account that will be referenced by subaccounts
   * The site will be used for account level CSM feature for enterprise
   */
  ACCOUNT_MASTER_CMS = 'ACCOUNT_MASTER_CMS',
  /** Rise.ai Siteless account management for Gift Cards and Store Credit. */
  RISE = 'RISE',
  /**
   * As part of the branded app new funnel, users now can create a meta site that will be branded app first.
   * There's a blank site behind the scene but it's blank).
   * The Mobile company will be the owner of this namespace.
   */
  BRANDED_FIRST = 'BRANDED_FIRST',
  /** Nownia.com Siteless account management for Ai Scheduling Assistant. */
  NOWNIA = 'NOWNIA',
  /**
   * UGC Templates are templates that are created by users for personal use and to sale to other users.
   * The Partners company owns this namespace.
   */
  UGC_TEMPLATE = 'UGC_TEMPLATE',
  /** Codux Headless Sites */
  CODUX = 'CODUX',
  /** Bobb - AI Design Creator. */
  MEDIA_DESIGN_CREATOR = 'MEDIA_DESIGN_CREATOR',
  /**
   * Shared Blog Site is a unique single site across Enterprise account,
   * This site will hold all Blog posts related to the Marketing product.
   */
  SHARED_BLOG_ENTERPRISE = 'SHARED_BLOG_ENTERPRISE',
  /** Standalone forms (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_FORMS = 'STANDALONE_FORMS',
  /** Standalone events (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_EVENTS = 'STANDALONE_EVENTS',
  /** MIMIR - Siteless account for MIMIR Ai Job runner. */
  MIMIR = 'MIMIR',
  /** Wix Twins platform. */
  TWINS = 'TWINS',
  /** Wix Nano. */
  NANO = 'NANO',
  /** Base44 headless sites. */
  BASE44 = 'BASE44',
  /** Wix Channels Sites */
  CHANNELS = 'CHANNELS',
  /** Nautilus platform. */
  NAUTILUS = 'NAUTILUS',
}

/** @enumType */
export type NamespaceWithLiterals =
  | Namespace
  | 'UNKNOWN_NAMESPACE'
  | 'WIX'
  | 'SHOUT_OUT'
  | 'ALBUMS'
  | 'WIX_STORES_TEST_DRIVE'
  | 'HOTELS'
  | 'CLUBS'
  | 'ONBOARDING_DRAFT'
  | 'DEV_SITE'
  | 'LOGOS'
  | 'VIDEO_MAKER'
  | 'PARTNER_DASHBOARD'
  | 'DEV_CENTER_COMPANY'
  | 'HTML_DRAFT'
  | 'SITELESS_BUSINESS'
  | 'CREATOR_ECONOMY'
  | 'DASHBOARD_FIRST'
  | 'ANYWHERE'
  | 'HEADLESS'
  | 'ACCOUNT_MASTER_CMS'
  | 'RISE'
  | 'BRANDED_FIRST'
  | 'NOWNIA'
  | 'UGC_TEMPLATE'
  | 'CODUX'
  | 'MEDIA_DESIGN_CREATOR'
  | 'SHARED_BLOG_ENTERPRISE'
  | 'STANDALONE_FORMS'
  | 'STANDALONE_EVENTS'
  | 'MIMIR'
  | 'TWINS'
  | 'NANO'
  | 'BASE44'
  | 'CHANNELS'
  | 'NAUTILUS';

/** Site transferred to another user. */
export interface SiteTransferred {
  /**
   * A previous owner id (user that transfers meta site).
   * @format GUID
   */
  oldOwnerId?: string;
  /**
   * A new owner id (user that accepts meta site).
   * @format GUID
   */
  newOwnerId?: string;
}

/** Soft deletion of the meta site. Could be restored. */
export interface SiteDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface DeleteContext {
  /** When the meta site was deleted. */
  dateDeleted?: Date | null;
  /** A status. */
  deleteStatus?: DeleteStatusWithLiterals;
  /**
   * A reason (flow).
   * @maxLength 255
   */
  deleteOrigin?: string;
  /**
   * A service that deleted it.
   * @maxLength 255
   */
  initiatorId?: string | null;
}

export enum DeleteStatus {
  UNKNOWN = 'UNKNOWN',
  TRASH = 'TRASH',
  DELETED = 'DELETED',
  PENDING_PURGE = 'PENDING_PURGE',
  PURGED_EXTERNALLY = 'PURGED_EXTERNALLY',
}

/** @enumType */
export type DeleteStatusWithLiterals =
  | DeleteStatus
  | 'UNKNOWN'
  | 'TRASH'
  | 'DELETED'
  | 'PENDING_PURGE'
  | 'PURGED_EXTERNALLY';

/** Restoration of the meta site. */
export interface SiteUndeleted {}

/** First publish of a meta site. Or subsequent publish after unpublish. */
export interface SitePublished {}

export interface SiteUnpublished {
  /**
   * A list of URLs previously associated with the meta site.
   * @maxLength 4000
   * @maxSize 10000
   */
  urls?: string[];
}

export interface SiteMarkedAsTemplate {}

export interface SiteMarkedAsWixSite {}

/**
 * Represents a service provisioned a site.
 *
 * Note on `origin_instance_id`:
 * There is no guarantee that you will be able to find a meta site using `origin_instance_id`.
 * This is because of the following scenario:
 *
 * Imagine you have a template where a third-party application (TPA) includes some stub data,
 * such as a product catalog. When you create a site from this template, you inherit this
 * default product catalog. However, if the template's product catalog is modified,
 * your site will retain the catalog as it was at the time of site creation. This ensures that
 * your site remains consistent with what you initially received and does not include any
 * changes made to the original template afterward.
 * To ensure this, the TPA on the template gets a new instance_id.
 */
export interface ServiceProvisioned {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * An instance id from which this instance is originated.
   * @maxLength 36
   */
  originInstanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
  /**
   * The origin meta site id
   * @format GUID
   */
  originMetaSiteId?: string | null;
}

export interface ServiceRemoved {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
}

/** Rename of the site. Meaning, free public url has been changed as well. */
export interface SiteRenamed {
  /**
   * A new meta site name (URL slug).
   * @maxLength 20
   */
  newSiteName?: string;
  /**
   * A previous meta site name (URL slug).
   * @maxLength 255
   */
  oldSiteName?: string;
}

/**
 * Hard deletion of the meta site.
 *
 * Could not be restored. Therefore it's desirable to cleanup data.
 */
export interface SiteHardDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface NamespaceChanged {
  /** A previous namespace. */
  oldNamespace?: NamespaceWithLiterals;
  /** A new namespace. */
  newNamespace?: NamespaceWithLiterals;
}

/** Assigned Studio editor */
export interface StudioAssigned {}

/** Unassigned Studio editor */
export interface StudioUnassigned {}

/**
 * Fired in case site URLs were changed in any way: new secondary domain, published, account slug rename, site rename etc.
 *
 * This is an internal event, it's not propagated in special events, because it's non-actionable. If you need to keep up
 * with sites and its urls, you need to listen to another topic/event. Read about it:
 *
 * https://bo.wix.com/wix-docs/rest/meta-site/meta-site---urls-service
 */
export interface SiteUrlChanged {}

/**
 * Used at the end of the deletion flow for both draft sites and when a user deletes a site.
 * Consumed by other teams to remove relevant data.
 */
export interface SitePurgedExternally {
  /**
   * @maxLength 2048
   * @maxSize 100
   * @deprecated
   * @targetRemovalDate 2025-04-15
   */
  appDefId?: string[];
}

/** Assigned Odeditor */
export interface OdeditorAssigned {}

/** Unassigned Odeditor */
export interface OdeditorUnassigned {}

/** Assigned Picasso editor */
export interface PicassoAssigned {}

/** Unassigned Picasso */
export interface PicassoUnassigned {}

/** Assigned Wixel */
export interface WixelAssigned {}

/** Unassigned Wixel */
export interface WixelUnassigned {}

/** Assigned StudioTwo */
export interface StudioTwoAssigned {}

/** Unassigned StudioTwo */
export interface StudioTwoUnassigned {}

/** Media from user domain is enabled. */
export interface UserDomainMediaEnabled {}

/** Media from user domain is disabled. */
export interface UserDomainMediaDisabled {}

/** Assigned Editorless */
export interface EditorlessAssigned {}

/** Unassigned Editorless */
export interface EditorlessUnassigned {}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateDeliveryProfileApplicationErrors =
  | {
      code?: 'DELIVERY_PROFILES_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DESTINATIONS_COLLISION';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SINGLE_DEFAULT_DELIVERY_PROFILE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'UNKNOWN_DELIVERY_CARRIER';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DELIVERY_CARRIER_MISSING_BACKUP_RATE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SUBDIVISIONS_UNSUPPORTED_FOR_COUNTRY';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type UpdateDeliveryProfileApplicationErrors =
  | {
      code?: 'DELIVERY_REGION_CANNOT_BE_UPDATED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DEFAULT_CANNOT_BE_UPDATED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type AddDeliveryRegionApplicationErrors =
  | {
      code?: 'DELIVERY_PROFILE_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DESTINATIONS_COLLISION';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'UNKNOWN_DELIVERY_CARRIER';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DELIVERY_CARRIER_MISSING_BACKUP_RATE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SUBDIVISIONS_UNSUPPORTED_FOR_COUNTRY';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type AddDeliveryRegionValidationErrors = {
  ruleName?: 'DELIVERY_REGION_NAME_ALREADY_EXISTS';
};
/** @docsIgnore */
export type UpdateDeliveryRegionApplicationErrors =
  | {
      code?: 'DELIVERY_PROFILE_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DELIVERY_REGION_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DESTINATIONS_COLLISION';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DELIVERY_CARRIER_CANNOT_BE_UPDATED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SUBDIVISIONS_UNSUPPORTED_FOR_COUNTRY';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type UpdateDeliveryRegionValidationErrors = {
  ruleName?: 'DELIVERY_REGION_NAME_ALREADY_EXISTS';
};
/** @docsIgnore */
export type GetDeliveryProfileByDeliveryRegionIdApplicationErrors = {
  code?: 'DELIVERY_REGION_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type RemoveDeliveryRegionApplicationErrors =
  | {
      code?: 'DELIVERY_PROFILE_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DELIVERY_REGION_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type AddDeliveryCarrierApplicationErrors =
  | {
      code?: 'DELIVERY_REGION_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CARRIER_ALREADY_EXISTS_IN_REGION';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DELIVERY_CARRIER_MISSING_BACKUP_RATE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'UNKNOWN_DELIVERY_CARRIER';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type AddDeliveryCarrierValidationErrors = {
  ruleName?: 'DELIVERY_CARRIER_MISSING_BACKUP_RATE';
};
/** @docsIgnore */
export type RemoveDeliveryCarrierApplicationErrors = {
  code?: 'DELIVERY_REGION_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type UpdateDeliveryCarrierApplicationErrors =
  | {
      code?: 'DELIVERY_REGION_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DELIVERY_CARRIER_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'UNKNOWN_DELIVERY_CARRIER';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'APP_ID_CANNOT_BE_UPDATED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type ListDeliveryCarriersApplicationErrors =
  | {
      code?: 'DELIVERY_CARRIER_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DELIVERY_PROFILE_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type UpdateCarrierSettingsApplicationErrors = {
  code?: 'DELIVERY_CARRIER_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type SetDeliveryCarrierActiveStatusApplicationErrors = {
  code?: 'DELIVERY_CARRIER_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
